import React, {  } from 'react';
import { createTrainer, destroyWorkoutRoutine, importRoutine, loadTrainerRoutines, publishWorkoutRoutine, setTrainerForRoutines } from 'redux/actions';
import { connect } from 'react-redux';
import { AsyncSpinnerButton } from 'components/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { modalPathFor, myRoutinesPath, routinesBase } from 'config/paths';
import withTrainer from 'partials/TrainerLoader';
import { dbRoutinesSelector, trainerForRoutinesSel } from 'redux/selectors';
import { useTranslation, withTranslation } from 'react-i18next';
import ActivityContainer from 'components/ActivityContainer';
import * as _ from 'lib/utilities';
import ResponsiveSections from 'components/ResponsiveSections';
import { WorkoutRoutineCard } from 'partials/OnboardingComponents';
import { NewRoutineModal } from 'partials/EditRoutineModals';
import { TrainerPaywall } from 'partials/PaywallWrapper';
import { TRAINING } from 'config/settings';
import IndexPage, { MyRecordDropdown } from 'partials/IndexPage';
import { User } from 'lib/classes';
import Connector from 'partials/WebsiteConnector';
import { ExportRoutinePDFModal } from 'partials/ExportPDFModal';
import LinkButton from 'components/LinkButton';
import { previewRoutinePdfUrlFor } from 'lib/api';
import { SlightEmphasisTip } from 'partials/Utilities';
import { CUSTOM_ROUTINE_DB_TIP } from 'config/tooltips';
import { useLocation } from 'react-router-dom';
import { CreateCustomDbModal, createCustomDbModalSuffix } from 'partials/CreateCustomDbModal';
import { Link } from 'components/Routing';
import { SlightEmphasisIconNote } from 'components/Typography';

const RoutineCardActions = ({ routine, t, importRoutine }) => {

    return (
        <React.Fragment>
            <LinkButton noShadow outlined color="primary" variant="icon-only" to={previewRoutinePdfUrlFor({ id: routine.id},true,false)} target="_blank">
                <FontAwesomeIcon icon={'info-circle'}></FontAwesomeIcon> 
            </LinkButton>
            <AsyncSpinnerButton 
                buttonProps={{
                    noShadow: true,
                    outlined: true,
                    color: 'primary',
                    label: t('Import'),
                    className: "ml5 import-db-routine-btn"
                }}
                icon={['far','file-import']}
                action={() => importRoutine(routine.id)}
            />
        </React.Fragment>
    )
}

const RoutinesCardList = ({ records: workoutRoutines, importRecord, topMessage, bottomMessage }) => {

    const { t } = useTranslation();
    const finalRoutines = _.filter(workoutRoutines,wrt => !wrt.inactive);

    return (
        <React.Fragment>
            {topMessage && <div style={ { margin: '0 auto', maxWidth: '700px' } }><div className="pa15">{topMessage}</div></div>}
            <ResponsiveSections sectionWidth={3}>
                {_.sortBy(finalRoutines,wrt => wrt.resolvedName(t)).map((routine) => (<WorkoutRoutineCard key={routine.id} routine={routine} t={t} actions={<RoutineCardActions routine={routine} t={t} importRoutine={importRecord} />} />))}
            </ResponsiveSections>
            <div className="text-center">{bottomMessage}</div>
        </React.Fragment>
    )
}

const mapDispatchToRoutineInitProps = dispatch => ({
    beforeAction: () => dispatch(createTrainer({ trainer: { trainerType: User.TRAINER_TYPES.app }, planType: 'training' })),
    afterPath: myRoutinesPath
})

const WorkoutRoutinesInit = connect(null, mapDispatchToRoutineInitProps)(Connector);

const CustomDbTip = ({ trainer }) => {
    const { t } = useTranslation();
    const { pathname } = useLocation();

    // Don't show if not admin or already has own routine db
    if (!trainer || !trainer.isTrainerAdmin() || trainer.hasOwnRoutineDb()) {
        return null;
    }

    const tipSeen = trainer.sawTooltip(CUSTOM_ROUTINE_DB_TIP);

    // If tip has been seen, show simple link
    if (tipSeen) {
        return (
            <Link className="underline" to={modalPathFor(createCustomDbModalSuffix, pathname)}>
                <FontAwesomeIcon icon="database" /> {t('Set Up Custom Database')}
            </Link>
        );
    }

    // If tip not seen, show full tip with button
    const customDbButton = (
        <LinkButton
            noShadow
            outlined
            color="primary"
            to={modalPathFor(createCustomDbModalSuffix, pathname)}
        >
            <FontAwesomeIcon icon="arrow-right"></FontAwesomeIcon> {t('Set Up Custom Database')}
        </LinkButton>
    );

    return (
        <SlightEmphasisTip
            tipName={CUSTOM_ROUTINE_DB_TIP}
            text={t('custom routine db tip')}
            btn={customDbButton}
            containerClassName="mt10 pa20"
        />
    );
}

export { WorkoutRoutinesInit }
class WorkoutRoutines extends React.Component {

    render() {
        const { routineImports, clearRoutineImports, dbRoutines, customDbRoutines, match: { params: { type }}, match, importRoutine, publishRoutine, loadRoutines, addRoutineImport, t, trainer, ...rest } = this.props;
        let extraProps = type === 'db' ? { Wrapper: ActivityContainer, successComponent: RoutinesCardList } : {};

        let topMessage = null;
        let bottomMessage = null;
        let actualDbRoutines = dbRoutines;

        if (type === 'db' && trainer) {
            // Use custom db routines if trainer has own routine db
            if (trainer.hasOwnRoutineDb()) {
                actualDbRoutines = trainer.customDbWorkoutRoutines();
                extraProps = null;

                // Show empty state message if no routines in custom db
                if (!actualDbRoutines || actualDbRoutines.length === 0) {
                    topMessage = (
                        <SlightEmphasisIconNote
                            text={t('custom db empty message')}
                        />
                    );
                }
            } else {
                // Show custom db tip if trainer doesn't have own routine db yet
                const tipComponent = <CustomDbTip trainer={trainer} />;
                const tipSeen = trainer.sawTooltip(CUSTOM_ROUTINE_DB_TIP);

                if (tipSeen) {
                    bottomMessage = tipComponent;
                } else {
                    topMessage = tipComponent;
                }
            }
        }

        return (
            <TrainerPaywall type={TRAINING}>
                <IndexPage
                    {...extraProps}
                    {...rest}
                    trainer={trainer}
                    importCount={routineImports}
                    clearImports={clearRoutineImports}
                    recordName={'WorkoutRoutines'}
                    recordNameP={t('routines')}
                    recordNameS={_.capitalize(t('routine'))}
                    dbRecords={actualDbRoutines}
                    match={match}
                    importRecord={importRoutine}
                    publishRecord={publishRoutine}
                    basePath={routinesBase}
                    load={loadRoutines}
                    addImport={addRoutineImport}
                    title={t("Workout Routines")}
                    NewModal={NewRoutineModal}
                    ExportModal={ExportRoutinePDFModal}
                    topMessage={topMessage}
                    bottomMessage={bottomMessage}
                    customDbAllowed={trainer.hasOwnRoutineDb()}
                    PersonalMenu={(props) => <MyRecordDropdown {...props} allowsDbImport={trainer.isTrainerAdmin() && trainer.hasOwnRoutineDb()} />}
                />
                <CreateCustomDbModal />
            </TrainerPaywall>
        )
    }
}

const mapStateToProps = state => ({
    dbRoutines: dbRoutinesSelector(state),
    trainerId: trainerForRoutinesSel(state)
})

const mapDispatchToProps = dispatch => ({
    loadRoutines: params => dispatch(loadTrainerRoutines(params)),
    importRoutine: (id,popup,data) => dispatch(importRoutine(id,popup,data)),
    destroy: (routine) => dispatch(destroyWorkoutRoutine(routine.id)),
    publishRoutine: (id) => dispatch(publishWorkoutRoutine(id)),
    selectTrainer: trainerId => dispatch(setTrainerForRoutines(trainerId))
})

export default withTrainer(false)(connect(mapStateToProps,mapDispatchToProps)(withTranslation()(WorkoutRoutines)));
